import React, { useState } from "react";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import FleetRegister from "../views/FleetRegister";
import MaintenanceExpiry from "../views/MaintenanceExpiry";
import MachineryTransfers from "../views/MachineryTransfers";
import StockOverview from "../views/StockOverview";
import StockLedger from "../views/StockLedger";
import ItemMasterHub from "../views/ItemMasterHub";
import AddMachineryModal from "../components/AddMachineryModal";
import CreateGRNModal from "../components/CreateGRNModal";
import { safeText } from "../utils/hubHelpers";
import "../styles/MachineryHub.css";

class HubErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, errorInfo) {
    console.error("MachineryHub Rendering Error:", error, errorInfo);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: "3rem", color: "#dc2626", background: "#fee2e2", margin: "2rem", borderRadius: 8 }}>
          <h3 style={{ fontWeight: 700, marginBottom: "1rem" }}>Machinery Hub UI Exception:</h3>
          <pre style={{ background: "#ffffff", padding: "1rem", borderRadius: 6, overflow: "auto" }}>
            {this.state.error?.toString()}
          </pre>
          <pre style={{ background: "#ffffff", padding: "1rem", borderRadius: 6, overflow: "auto", fontSize: "0.8rem" }}>
            {this.state.error?.stack}
          </pre>
        </div>
      );
    }
    return this.props.children;
  }
}

export default function MachineryHubLayout() {
  const [searchParams, setSearchParams] = useSearchParams();
  const currentTab = searchParams.get("tab") || "register"; // 'register' | 'maintenance' | 'transfers' | 'stock' | 'ledger' | 'items'
  const userDetails = useSelector((state) => state?.root?.userDetails);
  const [isAddMachineOpen, setIsAddMachineOpen] = useState(false);
  const [isCreateGrnOpen, setIsCreateGrnOpen] = useState(false);

  const handleTabChange = (tabId) => {
    setSearchParams({ tab: tabId });
  };

  const userInitial = typeof userDetails?.name === "string" && userDetails.name.length > 0
    ? userDetails.name.charAt(0).toUpperCase()
    : "U";

  return (
    <HubErrorBoundary>
      <div className="machinery-hub-root">
        {/* 1. Machinery Hub Global Topbar — Minimum Height Single Sleek Row */}
        <header className="machinery-navbar">
          <div style={{ display: "flex", alignItems: "center", width: "100%", maxWidth: 1720, margin: "0 auto", flexWrap: "nowrap", overflowX: "auto" }}>
            {/* Left: Brand Identity */}
            <div className="nav-brand-container" style={{ flexShrink: 0, marginRight: "1.25rem" }}>
              <h3 className="text-white mb-0" style={{ fontWeight: 800, fontSize: "16px", letterSpacing: "0.02em" }}>MACHINERY HUB</h3>
            </div>

            {/* Subtle Divider between Brand & Tabs */}
            <div style={{ width: 1, height: 22, background: "rgba(255, 255, 255, 0.15)", marginRight: "1.25rem", flexShrink: 0 }} />

            {/* Center: Module Navigation Tabs */}
            <div style={{ display: "flex", alignItems: "center", gap: "0.4rem", flexShrink: 0 }}>
              <button
                type="button"
                className={`nav-tab-button ${currentTab === "register" ? "active" : ""}`}
                onClick={() => handleTabChange("register")}
              >
                <span>🏢 Fleet Register</span>
              </button>

              <button
                type="button"
                className={`nav-tab-button ${currentTab === "maintenance" ? "active" : ""}`}
                onClick={() => handleTabChange("maintenance")}
              >
                <span>⚠️ Compliance & Services</span>
              </button>

              <button
                type="button"
                className={`nav-tab-button ${currentTab === "transfers" ? "active" : ""}`}
                onClick={() => handleTabChange("transfers")}
              >
                <span>🔁 Transfers</span>
              </button>

              <button
                type="button"
                className={`nav-tab-button ${currentTab === "stock" ? "active" : ""}`}
                onClick={() => handleTabChange("stock")}
              >
                <span>📦 Stock & Spares</span>
              </button>

              <button
                type="button"
                className={`nav-tab-button ${currentTab === "ledger" ? "active" : ""}`}
                onClick={() => handleTabChange("ledger")}
              >
                <span>📜 Stock Ledger</span>
              </button>

              <button
                type="button"
                className={`nav-tab-button ${currentTab === "items" ? "active" : ""}`}
                onClick={() => handleTabChange("items")}
              >
                <span>🏷️ Item Master</span>
              </button>
            </div>

            {/* Flexible Gap / Spacer that pushes Actions to the right and ensures at least 2rem between tabs & buttons */}
            <div style={{ flex: 1, minWidth: "2rem" }} />

            {/* Right: Actions & User Info (Distinct group with separation) */}
            <div style={{ display: "flex", alignItems: "center", gap: "0.65rem", flexShrink: 0, paddingLeft: "1.25rem", borderLeft: "1px solid rgba(255, 255, 255, 0.15)" }}>
              <button
                type="button"
                onClick={() => setIsCreateGrnOpen(true)}
                style={{
                  background: "linear-gradient(135deg, #2563eb, #1d4ed8)",
                  color: "#ffffff",
                  border: "none",
                  borderRadius: 6,
                  height: 34,
                  padding: "0 13px",
                  fontSize: "12.5px",
                  fontWeight: 600,
                  cursor: "pointer",
                  boxShadow: "0 1px 4px rgba(37, 99, 235, 0.25)",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "5px",
                  whiteSpace: "nowrap",
                  boxSizing: "border-box",
                }}
              >
                <span>📥</span> Inward / GRN
              </button>

              <button
                type="button"
                onClick={() => setIsAddMachineOpen(true)}
                style={{
                  background: "linear-gradient(135deg, #059669, #047857)",
                  color: "#ffffff",
                  border: "none",
                  borderRadius: 6,
                  height: 34,
                  padding: "0 13px",
                  fontSize: "12.5px",
                  fontWeight: 600,
                  cursor: "pointer",
                  boxShadow: "0 1px 4px rgba(5, 150, 105, 0.25)",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "5px",
                  whiteSpace: "nowrap",
                  boxSizing: "border-box",
                }}
              >
                <span>+</span> Register Machine
              </button>

              <div style={{ borderLeft: "1px solid rgba(255,255,255,0.15)", paddingLeft: "0.75rem", display: "flex", alignItems: "center", gap: "0.55rem" }}>
                <div style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                  <span style={{ fontSize: "12.5px", fontWeight: 700, color: "#fff", display: "block", lineHeight: 1.2 }}>
                    {safeText(userDetails?.name, "Operations User")}
                  </span>
                  <span style={{ fontSize: "11px", color: "#94a3b8" }}>
                    {safeText(
                      userDetails?.roleName ||
                      userDetails?.designationName ||
                      userDetails?.designation?.name ||
                      userDetails?.department?.name ||
                      userDetails?.role ||
                      userDetails?.department,
                      "SCM / Fleet"
                    )}
                  </span>
                </div>
                <div
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: "50%",
                    background: "#2563eb",
                    color: "#fff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: 700,
                    fontSize: "13px",
                    flexShrink: 0,
                  }}
                >
                  {userInitial}
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* 2. Main Content Workspace */}
        <main className="machinery-main-content">
          {currentTab === "register" && (
            <FleetRegister onOpenAddMachine={() => setIsAddMachineOpen(true)} />
          )}
          {currentTab === "maintenance" && <MaintenanceExpiry />}
          {currentTab === "transfers" && <MachineryTransfers />}
          {currentTab === "stock" && <StockOverview onOpenCreateGrn={() => setIsCreateGrnOpen(true)} />}
          {currentTab === "ledger" && <StockLedger onOpenCreateGrn={() => setIsCreateGrnOpen(true)} />}
          {currentTab === "items" && <ItemMasterHub />}
        </main>

        {/* Register Machine Modal */}
        <AddMachineryModal
          isOpen={isAddMachineOpen}
          toggle={() => setIsAddMachineOpen(!isAddMachineOpen)}
          onSuccess={() => {
            window.location.reload();
          }}
        />

        {/* Inward Goods / Create GRN Modal */}
        <CreateGRNModal
          isOpen={isCreateGrnOpen}
          toggle={() => setIsCreateGrnOpen(!isCreateGrnOpen)}
          onSuccess={() => {
            window.location.reload();
          }}
        />
      </div>
    </HubErrorBoundary>
  );
}
